import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { injectQuery } from '@tanstack/angular-query-experimental';

// PrimeNG
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageService } from 'primeng/api';
import { TreeSelectModule } from 'primeng/treeselect';
import { MultiSelectModule } from 'primeng/multiselect';

// Kanzi
import * as fromCustomer from '@kanzi-apes/kanzi-customer';
import { CustomerService } from '@kanzi-apes/kanzi-customer';
import * as fromAuth from '@kanzi-apes/kanzi-auth';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { UbicacionesQueriesService } from '../../../queries/ubicaciones-queries.service';
import { LocationCreateModel, CustomerListModel } from '@kanzi-apes/kanzi-models';
import { Store } from '@ngrx/store';

/**
 * Ubicación Edit Component
 *
 * Edit an existing ubicación with parent selection
 */
@Component({
    selector: 'kanzi-apes-ubicacion-edit',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        CardModule,
        ButtonModule,
        InputTextModule,
        InputTextareaModule,
        DropdownModule,
        MessageModule,
        SkeletonModule,
        TreeSelectModule,
        MultiSelectModule,
        KanziUiModule,
    ],
    providers: [MessageService],
    templateUrl: './ubicacion-edit.component.html',
    styleUrls: ['./ubicacion-edit.component.scss']
})
export class UbicacionEditComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private queries = inject(UbicacionesQueriesService);
  private messageService = inject(MessageService);
  private storeData = inject(Store);
  private readonly customerService = inject(CustomerService);

  customerSelected = this.storeData.selectSignal(fromCustomer.getCustomerSelected);
  userLogged = this.storeData.selectSignal(fromAuth.getAuthUser);

  /**
   * Zone type options for dropdown selection.
   * Maps backend zone type values to user-friendly Spanish labels.
   */
  readonly zoneTypeOptions = [
    { label: 'Recepción', value: 'RECEPTION' },
    { label: 'Recepción de Intercambio', value: 'EXCHANGE_RECEPTION' },
    { label: 'Almacenamiento', value: 'STORAGE' },
    { label: 'Almacenamiento de Buffer', value: 'BUFFER_STORAGE' },
    { label: 'Zona de Tránsito', value: 'TRANSIT' },
    { label: 'Despacho', value: 'DISPATCH' },
    { label: 'Despacho de Intercambio', value: 'EXCHANGE_DISPATCH' },
    { label: 'Control', value: 'CONTROL' },
    { label: 'Control de Pérdidas', value: 'LOSS_CONTROL' },
    { label: 'Destrucción', value: 'DESTRUCTION' },
    { label: 'Donación', value: 'DONATION' },
  ];

  // Determines if customer multi-select should be shown
  showCustomerSelector = computed(
    () => this.userLogged()?.configs?.customer?.parent == null
  );

  // Query to load available customers (enabled only when multi-select is needed)
  customersQuery = injectQuery<CustomerListModel, Error>(() => ({
    queryKey: ['customers', { page_size: 1000 }],
    queryFn: () =>
      lastValueFrom(this.customerService.listCustomers({ page_size: 1000 })),
    enabled: this.showCustomerSelector(),
    staleTime: 1000 * 60 * 5,
  }));

  // Selected customer IDs for multi-select (plain property for ngModel compatibility)
  selectedCustomerIds: number[] = [];

  // Route params
  ubicacionId = signal<number | null>(null);
  isQueryEnabled = computed(() => this.ubicacionId() !== null);

  // Form model
  ubicacion: LocationCreateModel = {
    name: '',
    display_name: '',
    external_id: '',
    description: '',
    customer_ids: [],
    parent_id: 0,
    type_id: undefined,
    longitude: undefined,
    latitude: undefined,
    zone_type: undefined,
  };

  // Selected parent from tree
  selectedParent = signal<any>(null);

  // Selected type for reactive filtering
  selectedTypeId = signal<number | undefined>(undefined);

  // Queries
  ubicacionQuery = this.queries.useUbicacionDetail(
    this.ubicacionId,
    this.isQueryEnabled
  );
  typesQuery = this.queries.useLocationTypes(signal({}));

  // Computed: Get parent type_id from selected type
  parentTypeId = computed(() => {
    const selectedId = this.selectedTypeId();
    if (!selectedId) return undefined;

    const types = this.typesQuery.data()?.results || [];
    const selectedType = types.find((t) => t.id === selectedId);
    return selectedType?.parent?.id;
  });

  // Computed: Filters for parent locations based on selected type
  parentFilters = computed(() => {
    const typeId = this.parentTypeId();
    return typeId ? { type_id: typeId } : null;
  });

  // Query for parent tree (only enabled when parent type is available)
  hasSelectedType = computed(() => {
    const parentId = this.parentTypeId();
    return parentId !== undefined && parentId !== null;
  });
  treeQuery = this.queries.useUbicacionesTree(
    this.parentFilters,
    this.hasSelectedType
  );

  updateMutation = this.queries.useUpdateUbicacion();

  // Location types for dropdown
  locationTypes = computed(() => {
    const results = this.typesQuery.data()?.results || [];
    return results
      .filter((t) => t.name !== 'WAREHOUSE')
      .map((t) => ({ label: t.name, value: t.id }));
  });

  // Parent tree for TreeSelect (exclude current ubicación and its descendants)
  parentTree = computed(() => {
    const tree = this.treeQuery.data();
    const currentId = this.ubicacionId();
    if (!tree || !currentId) return [];

    // Filter out current node and its descendants
    return this.convertToTreeSelectNodes(tree, currentId);
  });

  // Loading states
  isLoading = computed(
    () => this.ubicacionQuery.isPending() || this.ubicacionQuery.isFetching()
  );
  isTypesLoading = computed(() => this.typesQuery.isPending());
  isTreeLoading = computed(() => this.treeQuery.isPending());
  isUpdating = computed(() => this.updateMutation.isPending());

  constructor() {
    // Debug effect to track reactive changes
    effect(() => {
      console.log('🔍 Edit Debug Info:', {
        selectedTypeId: this.selectedTypeId(),
        parentTypeId: this.parentTypeId(),
        hasSelectedType: this.hasSelectedType(),
        parentFilters: this.parentFilters(),
        treeQueryEnabled: this.hasSelectedType(),
        treeData: this.treeQuery.data()?.length,
      });
    });

    // Get ID from route
    this.route.params.subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.ubicacionId.set(parseInt(id, 10));
      }
    });

    // Load data into form when query succeeds
    effect(() => {
      const data = this.ubicacionQuery.data();
      if (data) {
        this.ubicacion = {
          id: data.id,
          name: data.name || '',
          display_name: data.display_name || data.name || '',
          external_id: data.external_id ? String(data.external_id) : '',
          description: data.description || '',
          type_id: data.type?.id,
          parent_id: data.parent?.id || 0,
          customer_ids: [],
          longitude: data.longitude,
          latitude: data.latitude,
          zone_type: data.zone_type ?? undefined,
        };

        // Pre-populate multi-select with existing customer IDs
        const customerIds = data.customers?.map((c: any) => c.id).filter((id: any): id is number => id !== undefined) ||
                            data.customer?.map((c: any) => c.id).filter((id: any): id is number => id !== undefined) ||
                            (data.customer_id ? [data.customer_id] : []);
        this.selectedCustomerIds = [...customerIds];
        this.ubicacion.customer_ids = customerIds;

        // Set selected type to trigger parent filtering
        if (data.type?.id) {
          this.selectedTypeId.set(data.type.id);
        }

        // Set selected parent in TreeSelect
        if (data.parent) {
          this.selectedParent.set({
            key: data.parent.toString(),
          });
        }
      }
    });
  }

  /**
   * Convert LocationTreeNode[] to TreeSelect nodes (excluding current node)
   */
  private convertToTreeSelectNodes(locations: any[], excludeId: number): any[] {
    return locations
      .filter((loc) => loc.id !== excludeId)
      .map((loc) => this.toTreeSelectNode(loc, excludeId))
      .filter((node) => node !== null);
  }

  /**
   * Recursively convert to TreeSelect node format
   */
  private toTreeSelectNode(location: any, excludeId: number): any | null {
    if (location.id === excludeId) return null;

    const children =
      location.children
        ?.map((child: any) => this.toTreeSelectNode(child, excludeId))
        .filter((node: any) => node !== null) || [];

    return {
      key: location.id.toString(),
      label: `${location.name} (${
        location.type?.display_name || location.type?.name
      })`,
      data: location.id,
      children,
    };
  }

  /**
   * Handle type change to update parent filters
   */
  onTypeChange(typeId: number | undefined): void {
    this.selectedTypeId.set(typeId);
    this.ubicacion.type_id = typeId;
    // Reset selected parent when type changes
    this.selectedParent.set(null);
  }

  /**
   * Validate form
   */
  private isFormValid(): boolean {
    if (!this.ubicacion.name.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El nombre es requerido',
      });
      return false;
    }

    if (!this.ubicacion.type_id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El tipo de ubicación es requerido',
      });
      return false;
    }

    if (this.showCustomerSelector() && this.selectedCustomerIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Debe seleccionar al menos un cliente',
      });
      return false;
    }

    return true;
  }

  /**
   * Update ubicación
   */
  onSave(): void {
    if (!this.isFormValid() || !this.ubicacionId()) return;

    const dataToSave: LocationCreateModel = {
      ...this.ubicacion,
      display_name: this.ubicacion.display_name || this.ubicacion.name,
      parent_id: this.selectedParent()?.key
        ? parseInt(this.selectedParent().key, 10)
        : this.ubicacion.parent_id,
      customer_ids: this.showCustomerSelector()
        ? this.selectedCustomerIds
        : this.ubicacion.customer_ids,
      zone_type: this.ubicacion.zone_type || null,
    };

    this.updateMutation.mutate(dataToSave, {
      onSuccess: (data) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Ubicación actualizada correctamente',
        });
        setTimeout(() => {
          this.router.navigate(['/locations/ubicaciones/detail', data.id]);
        }, 1000);
      },
      onError: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.message || 'Error al actualizar la ubicación',
        });
      },
    });
  }

  /**
   * Cancel and go back
   */
  onCancel(): void {
    const id = this.ubicacionId();
    if (id) {
      this.router.navigate(['/locations/ubicaciones/detail', id]);
    } else {
      this.router.navigate(['/locations/ubicaciones/list']);
    }
  }
}
